import { Skeleton } from "@/components/atoms/skeleton/Skeleton";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { Platform } from "react-native";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useIsAdhaJourney, useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { FAQScreenWeb } from "@/modules/faq/components/FAQScreenWeb";
import { FAQEmptyState } from "@/modules/faq/components/FAQEmptyState";
import { FAQSearchInput } from "@/modules/faq/components/FAQSearchInput";
import { FaqList } from "@/modules/faq/components/FaqList";
import { useFaqs } from "@/modules/faq/hooks/useFaqs";
import { searchFaqItems } from "@/modules/faq/utils/faqSearch";
import { useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, TextInput, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export default function FAQCategoryScreen() {
  // Use WebAppLayout on web platform
  if (Platform.OS === "web") {
    return <FAQScreenWeb />;
  }

  // Native implementation
  const colors = useUIColors();
  const { t } = useTranslation();
  const { category } = useLocalSearchParams<{ category?: string }>();
  const insets = useSafeAreaInsets();
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<TextInput>(null);
  // Same "State Awareness" as FAQScreenWeb — must match the flow used to
  // fetch categories on the main FAQ screen, otherwise an ADHA session's
  // category names here won't match app/faq.tsx's ADHA-tagged category list.
  const isAdhaJourney = useIsAdhaJourney();
  const { categories, isLoading } = useFaqs(isAdhaJourney);

  const allItems = useMemo(() => {
    if (!category) return [];
    const group = categories.find((c) => c.category === category);
    return group?.items || [];
  }, [categories, category]);

  const filteredItems = useMemo(() => {
    return searchFaqItems(allItems, searchText, {
      includeCategory: false, // Category already filtered, no need to search it
      sortByCategory: false, // Maintain original order
    });
  }, [allItems, searchText]);

  const hasSearch = searchText.trim().length > 0;
  const hasSearchResults = filteredItems.length > 0;
  const categoryReady = Boolean(category) && !isLoading;
  const categoryOpenTrackedRef = useRef(false);

  useEffect(() => {
    if (!categoryReady || categoryOpenTrackedRef.current) {
      return;
    }
    categoryOpenTrackedRef.current = true;
    trackCatalogEvent({
      screen: DT_SCREEN.FAQ,
      actionName: 'faq_category_open',
      eventType: 'action',
      properties: { p_sectionName: category },
    });
    if (isAdhaJourney) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.FAQ,
        actionName: ALMETLAA_ACTION.FAQ.CATEGORY_CLICK,
        eventType: 'action',
        properties: { p_sectionName: category },
      });
    }
  }, [category, categoryReady, isAdhaJourney]);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader title={category || ""} />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom, 24) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        <View style={styles.content}>
          <FAQSearchInput
            value={searchText}
            onChangeText={setSearchText}
            inputRef={searchInputRef}
          />

          {/* Search Results or Loading */}
          {hasSearch ? (
            isLoading ? (
              <View style={styles.searchSkeletons}>
                {[...Array(3)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    height={64}
                    borderRadius={16}
                    style={{ width: "100%" }}
                  />
                ))}
              </View>
            ) : hasSearchResults ? (
              <FaqList items={filteredItems} />
            ) : (
              <FAQEmptyState isSearchNoResults={true} />
            )
          ) : isLoading ? (
            <View style={styles.loadingPlaceholder}>
              <Skeleton height={20} width="100%" style={{ marginBottom: 8 }} />
              <Skeleton height={20} width="100%" style={{ marginBottom: 8 }} />
              <Skeleton height={20} width="100%" />
            </View>
          ) : (
            <FaqList items={allItems} />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingTop: 32,
    paddingHorizontal: 16,
    gap: 24,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  loadingPlaceholder: {
    height: 120,
    borderRadius: 16,
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  searchSkeletons: {
    gap: 12,
  },
});
